/** Items and information to calculate tax for. */
export interface CalculateTaxRequest {
  /**
   * 3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.
   * @format CURRENCY
   */
  currency?: string;
  /**
   * Array of addresses. Each line item can individually reference the address to apply with `lineItems.addressIndex`.
   * @minSize 1
   * @maxSize 3
   */
  addresses?: Address[];
  /**
   * Line items to calculate tax for.
   * @minSize 1
   * @maxSize 300
   */
  lineItems?: LineItem[];
}

/** Wix common address format for physical address to use if you plan to store addresses in your service. */
export interface Address {
  /**
   * 2-letter country code in [ISO-3166 alpha-1](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#Officially_assigned_code_elements) format.
   * @format COUNTRY
   */
  country?: string | null;
  /**
   * Subdivision (such as state, prefecture, or province) in [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-2) format.
   * @maxLength 50
   */
  subdivision?: string | null;
  /**
   * City name.
   * @maxLength 100
   */
  city?: string | null;
  /**
   * Postal or zip code.
   * @maxLength 50
   */
  postalCode?: string | null;
  /**
   * Main address line, usually street and number as free text.
   * @maxLength 200
   */
  addressLine1?: string | null;
  /**
   * Free text providing more detailed address information, such as apartment, suite, or floor.
   * @maxLength 200
   */
  addressLine2?: string | null;
}

/** Line items to calculate tax for. */
export interface LineItem {
  /**
   * Line item ID.
   * @minLength 1
   * @maxLength 100
   */
  _id?: string;
  /**
   * Line item name to display.
   * @maxLength 250
   */
  itemName?: string | null;
  /**
   * Line item quantity.
   * @min 1
   * @max 100000
   */
  quantity?: number;
  /**
   * Line item price.
   * @format DECIMAL_VALUE
   * @decimalValue options { lte:1000000000000000 }
   */
  price?: string;
  /**
   * Stock keeping unit for this line item. Learn more about [SKUs](https://www.wix.com/encyclopedia/definition/stock-keeping-unit-sku).
   * @maxLength 40
   */
  itemCode?: string | null;
  /**
   * Tax group ID for this line item. If not provided, the default tax group applies.
   * @format GUID
   */
  taxGroupId?: string | null;
  /** Whether tax is included in the price. */
  taxIncludedInPrice?: boolean;
  /** Index of the address from `addresses` to use to calculate tax for this specific line item. The index is zero-based. */
  addressIndex?: AddressIndex;
  /**
   * Tax region ID for this line item.
   * @format GUID
   */
  taxRegionId?: string | null;
}

/** Index of the address from `addresses` to use to calculate tax for this specific line item. The index is zero-based. */
export interface AddressIndex extends AddressIndexAddressIndexOptionsOneOf {
  /** Single address to use for a sale location when only one address is required for tax calculations. The index is zero-based. */
  singleAddress?: number;
  /**
   * Multiple addresses to use for a sale. For example, some tax calculations may require both the address where an item is shipped from,
   * as well as the address the item is shipped to.
   */
  multipleAddresses?: MultipleAddresses;
}

/** @oneof */
export interface AddressIndexAddressIndexOptionsOneOf {
  /** Single address to use for a sale location when only one address is required for tax calculations. The index is zero-based. */
  singleAddress?: number;
  /**
   * Multiple addresses to use for a sale. For example, some tax calculations may require both the address where an item is shipped from,
   * as well as the address the item is shipped to.
   */
  multipleAddresses?: MultipleAddresses;
}

/**
 * Multiple addresses to use for a sale. For example, some tax calculations may require both the address where an item
 * is shipped from, as well as the address the item is shipped to.
 */
export interface MultipleAddresses {
  /** Index of the origin address. */
  origin?: number;
  /** Index of the destination address. */
  destination?: number;
}

/** Calculated tax. */
export interface CalculateTaxResponse {
  /**
   * 3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.
   * If this code doesn't match the `currency` passed in the request, then the response is invalid.
   * @format CURRENCY
   */
  currency?: string;
  /** Summary of the tax calculated. */
  taxSummary?: TaxSummary;
  /**
   * Details of each tax applied to each line item.
   * @minSize 1
   * @maxSize 300
   */
  lineItemTaxDetails?: LineItemTaxDetails[];
}

export interface TaxSummary {
  /**
   * Total price for all line items.
   * @format DECIMAL_VALUE
   * @decimalValue options { maxScale:4 }
   */
  totalAmount?: string;
  /**
   * Total amount of tax calculated for all line items.
   * Note that due to rounding, `totalTax` may not equal the sum of `lineItemTaxDetails.taxSummary.taxAmount`.
   * @format DECIMAL_VALUE
   * @decimalValue options { maxScale:4 }
   */
  totalTax?: string;
  /**
   * Total taxable amount for all line items.
   * @format DECIMAL_VALUE
   * @decimalValue options { maxScale:4 }
   */
  totalTaxableAmount?: string;
  /**
   * Total amount of `totalTax` that is included in price. Applies to line items with `taxIncludedInPrice` set to `true`.
   * @format DECIMAL_VALUE
   * @decimalValue options { maxScale:4 }
   */
  totalTaxIncludedInPrice?: string | null;
}

/** Tax details for a specific line item. */
export interface LineItemTaxDetails {
  /**
   * Line item ID.
   * @minLength 1
   * @maxLength 100
   */
  _id?: string;
  /**
   * Line item name to display.
   * @maxLength 250
   */
  itemName?: string | null;
  /**
   * Line item quantity.
   * @min 1
   * @max 100000
   */
  quantity?: number;
  /**
   * Array of each tax applied, grouped by `jurisdiction`, `jurisdictionType`, `taxName`, `rate`, and `taxType`.
   * @maxSize 7
   */
  taxBreakdown?: TaxBreakdown[];
  /** Summary of this line item's total price and tax. */
  taxSummary?: LineItemTaxSummary;
}

/** A detailed description of all the tax authorities applied on this item. */
export interface TaxBreakdown {
  /**
   * Jurisdiction that taxes were calculated for.
   * @maxLength 200
   */
  jurisdiction?: string | null;
  /**
   * Non-taxable amount of the price.
   * @format DECIMAL_VALUE
   * @decimalValue options { maxScale:4, gte:0 }
   */
  nonTaxableAmount?: string | null;
  /**
   * Tax rate used for this jurisdiction, as a decimal. For example, 10% tax is `"0.1000"` and 200% tax is `"2.0000"`.
   * @format DECIMAL_VALUE
   * @decimalValue options { maxScale:6, gte:0 }
   */
  rate?: string | null;
  /**
   * Amount of this tax calculated for this jurisdiction.
   * @format DECIMAL_VALUE
   * @decimalValue options { maxScale:4 }
   */
  taxAmount?: string | null;
  /**
   * Taxable amount of the price.
   * @format DECIMAL_VALUE
   * @decimalValue options { maxScale:4, gte:0 }
   */
  taxableAmount?: string | null;
  /**
   * Type of tax that was calculated. For example, `"Sales Tax"`, `"Income Tax"`, `"Value Added Tax"`, etc.
   * @maxLength 200
   */
  taxType?: string | null;
  /**
   * Name of the tax that was calculated. For example, `"NY State Sales Tax"`, `"Quebec GST"`, etc.
   * @maxLength 200
   */
  taxName?: string | null;
  /** Type of jurisdiction that taxes were calculated for. For example, `"State"`, `"County"`, `"City"`, `"Special"`, etc. */
  jurisdictionType?: JurisdictionType;
}

/** Type of jurisdiction that taxes were calculated for. For example, `"State"`, `"County"`, `"City"`, `"Special"`, etc. */
export enum JurisdictionType {
  UNDEFINED = 'UNDEFINED',
  COUNTRY = 'COUNTRY',
  STATE = 'STATE',
  COUNTY = 'COUNTY',
  CITY = 'CITY',
  SPECIAL = 'SPECIAL',
}

export interface LineItemTaxSummary {
  /**
   * Total price for this line item.
   * To determine the price for each individual unit of this line item, divide by `quantity`.
   * @format DECIMAL_VALUE
   * @decimalValue options { lte:1000000000000000 }
   */
  fullPrice?: string | null;
  /**
   * The total amount of tax calculated for this line item.
   * @format DECIMAL_VALUE
   * @decimalValue options { maxScale:4 }
   */
  taxAmount?: string;
  /**
   * Total taxable amount for this line item.
   * @format DECIMAL_VALUE
   * @decimalValue options { maxScale:4 }
   */
  taxableAmount?: string;
}

export interface TaxCalculationConfig {
  /**
   * **Required.** Base URI where the endpoints are called. Wix appends the endpoint path to the base URI.
   *
   * For example, to call the Calculate Tax endpoint at `https://my-tax-calc.com/v1/calculateTax`, the base URI you provide here is `"https://my-tax-calc.com/"`.
   */
  deploymentUri?: SpiBaseUri;
  /**
   * Display name of the tax calculator.
   * @minLength 1
   * @maxLength 200
   */
  calculatorDisplayName?: string;
  /**
   * List of countries, in [ISO-3166 alpha-1](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#Officially_assigned_code_elements) format, that the calculator does not support.
   * @format COUNTRY
   * @maxSize 195
   */
  unsupportedCountries?: string[];
}

/** Base URI configuration for a service plugin. Wix uses these URIs to call your service plugin methods. */
export interface SpiBaseUri {
  /**
   * Base URI for your service plugin. Wix appends each method's path to this URI.
   *
   * For example, to receive requests at `https://my-app.com/v1/my-method`, set this field to `https://my-app.com/`.
   * @minLength 6
   * @maxLength 2048
   */
  baseUri?: string;
  /** Alternative URIs for specific methods. Use these to override the default URI for individual methods. */
  alternativeUris?: AlternativeUri[];
}

/** Custom URI for the specified service plugin method. */
export interface AlternativeUri {
  /**
   * Name of the method to call at the absolute URI, in PascalCase. For example, to call [Get Shipping Rates](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/extensions/shipping-rates/shipping-rates-integration-service-plugin/get-shipping-rates) on an alternative URI, specify `GetShippingRates`.
   * @minLength 3
   * @maxLength 128
   */
  methodName?: string;
  /**
   * Absolute URI that Wix calls for this method. Wix doesn't append any path to this URI.
   *
   * The URI must begin with `https://`, such as `https://my-app.com/v1/my-custom-method`.
   * @minLength 6
   * @maxLength 2048
   */
  absoluteUri?: string;
}

/**
 * this message is not directly used by any service,
 * it exists to describe the expected parameters that SHOULD be provided to invoked Velo methods as part of open-platform.
 * e.g. SPIs, event-handlers, etc..
 * NOTE: this context object MUST be provided as the last argument in each Velo method signature.
 *
 * Example:
 * ```typescript
 * export function wixStores_onOrderCanceled({ event, metadata }: OrderCanceledEvent) {
 * ...
 * }
 * ```
 */
export interface Context {
  /** A unique identifier of the request. You may print this ID to your logs to help with future debugging and easier correlation with Wix's logs. */
  requestId?: string | null;
  /**
   * [ISO 4217](https://en.wikipedia.org/wiki/ISO_4217) 3-letter currency code.
   * @format CURRENCY
   */
  currency?: string | null;
  /** An object that describes the identity that triggered this request. */
  identity?: IdentificationData;
  /** A string representing a language and region in the format of `"xx-XX"`. First 2 letters represent the language code according to ISO 639-1. This is followed by a dash "-", and then a by 2 capital letters representing the region according to ISO 3166-2. For example, `"en-US"`. */
  languages?: string[];
  /**
   * The service provider app's instance ID.
   * @format GUID
   */
  instanceId?: string | null;
}

export enum IdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: IdentityType;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}
